import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

export default function ProtectedCertificateModal({ open, onClose }) {
  const theme = useTheme();
  const [isWindowBlurred, setIsWindowBlurred] = useState(false);

  // Security listeners: block printing, saving, and blur image when window loses focus (e.g., snipping tool activated)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Block Ctrl+P / Cmd+P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        e.stopPropagation();
      }
      // Block Ctrl+S / Cmd+S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopPropagation();
      }
      // Deter PrintScreen
      if (e.key === 'PrintScreen') {
        setIsWindowBlurred(true);
        setTimeout(() => setIsWindowBlurred(false), 2000);
      }
    };

    const handleWindowBlur = () => {
      // When user clicks away or launches external screen capture tool
      setIsWindowBlurred(true);
    };

    const handleWindowFocus = () => {
      setIsWindowBlurred(false);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#071626' : '#FFFFFF',
          border: `1.5px solid ${alpha('#059669', 0.3)}`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        },
      }}
    >
      <style>
        {`
          @media print {
            .protected-cert-container {
              display: none !important;
            }
          }
        `}
      </style>

      {/* Dialog Header */}
      <DialogTitle
        sx={{
          py: 2,
          px: 3,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(5, 150, 105, 0.05)',
          borderBottom: `1px solid ${alpha('#059669', 0.2)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              bgcolor: alpha('#059669', 0.15),
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VerifiedUserRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
              Certificate of Registration
            </Typography>
            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700 }}>
              Ogun State Government • Registration No. 7425
            </Typography>
          </Box>
        </Stack>

        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content / Protected Viewer */}
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Anti-tamper banner */}
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha('#0B3B60', 0.06),
            border: `1px solid ${alpha('#0B3B60', 0.12)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <LockRoundedIcon sx={{ fontSize: 18, color: '#0B3B60', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Protected official credential. Right-click, saving, drag-and-drop, and reproduction are disabled to safeguard society legal records.
          </Typography>
        </Box>

        {/* Certificate Display Container with multi-layered protection */}
        <Box
          className="protected-cert-container"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 580,
            mx: 'auto',
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            bgcolor: '#FFFFFF',
          }}
        >
          {/* Base Certificate Image */}
          <Box
            component="img"
            src="/assets/images/registration_certificate.png"
            alt="Official Certificate of Registration"
            draggable={false}
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              pointerEvents: 'none',
              userSelect: 'none',
              filter: isWindowBlurred ? 'blur(16px)' : 'none',
              transition: 'filter 0.2s ease',
            }}
          />

          {/* Repeating diagonal SVG watermark overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 2,
              opacity: 0.18,
              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='260' height='160'><text x='20' y='90' fill='%23059669' font-size='12' font-family='sans-serif' font-weight='bold' transform='rotate(-28 130 80)'>MUTUAL TRUST CMS • REG 7425</text></svg>")`,
              backgroundRepeat: 'repeat',
            }}
          />

          {/* Transparent Glass Interaction Shield (absorbs all touch/pointer interactions) */}
          <Box
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 3,
              cursor: 'default',
              WebkitTouchCallout: 'none',
              userSelect: 'none',
            }}
          />
        </Box>

        {/* Official Legal Registry Details */}
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', display: 'block', mb: 1, letterSpacing: 0.5 }}>
            OFFICIAL REGISTRATION PARTICULARS
          </Typography>

          <Stack spacing={0.75}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Registered Name:
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>
                Mutual Trust Adigbe (Obafemi/Owode) 'FUG' Cooperative Multipurpose Society Limited
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Registration Number:
              </Typography>
              <Chip label="OG/NO. 7425" size="small" sx={{ fontWeight: 800, fontSize: 11, height: 22, bgcolor: alpha('#059669', 0.12), color: '#059669' }} />
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Enabling Statute:
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>
                Section 7(1), Co-operative Societies Law, Cap. 26, Ogun State Laws (1978)
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Registration Date:
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>
                25th October, 2007 • Abeokuta
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Issuing Authority:
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>
                Director of Co-operative Services, Ogun State of Nigeria
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SecurityRoundedIcon sx={{ fontSize: 18, color: '#059669' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Official Society Record Verified
          </Typography>
        </Stack>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#0B3B60' }}>
          Close Verification
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProtectedCertificateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
